import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { useUser } from '@clerk/react';
import log from 'loglevel';

import { useUpdateTaskGroupParticipants } from './useUpdateTaskGroupParticipants';
import { URL_PARAMS } from '../../hooks';
import { useCustomToast } from '../../hooks/useCustomToast';
import { useInboxEntriesStore } from '../../store';
import {
  useDoBoxFilterSortStore,
  useDoBoxLeftTaskGroupStore,
} from '../../store/DoBox';
import { isOwnerRemovalForbiddenError } from '../../utils/graphqlErrors';

interface UseLeaveTaskGroupReturn {
  leaveTaskGroup: (taskGroupId: string) => Promise<boolean>;
  isLeaving: boolean;
}

export function useLeaveTaskGroup(): UseLeaveTaskGroupReturn {
  const { t } = useTranslation();
  const { user } = useUser();
  const [, setSearchParams] = useSearchParams();
  const { errorMessage } = useCustomToast();
  const { handleUpdateTaskGroupParticipants } =
    useUpdateTaskGroupParticipants();

  const removeTaskGroup = useInboxEntriesStore(
    (s) => s.taskManager.taskGroups.removeTaskGroup,
  );
  const markLeft = useDoBoxLeftTaskGroupStore((s) => s.markLeft);

  const [isLeaving, setIsLeaving] = useState(false);

  const leaveTaskGroup = useCallback(
    async (taskGroupId: string): Promise<boolean> => {
      if (!user?.id) {
        return false;
      }

      setIsLeaving(true);

      try {
        await handleUpdateTaskGroupParticipants(taskGroupId, {
          addIds: [],
          removeIds: [user.id],
        });
        removeTaskGroup(taskGroupId);
        useDoBoxFilterSortStore.getState().removeTaskGroupSnapshot(taskGroupId);
        markLeft(taskGroupId);
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete(URL_PARAMS.TASK_GROUP_ID);
            return next;
          },
          { replace: true },
        );
        return true;
      } catch (error) {
        log.error('Error leaving task group:', error);
        const message = isOwnerRemovalForbiddenError(error)
          ? t('components.taskGroup.cannotRemoveOwner')
          : t('components.inboxEntryDetailsModal.participantsModal.saveError');
        errorMessage(message);
        return false;
      } finally {
        setIsLeaving(false);
      }
    },
    [
      user?.id,
      handleUpdateTaskGroupParticipants,
      removeTaskGroup,
      markLeft,
      setSearchParams,
      t,
      errorMessage,
    ],
  );

  return { leaveTaskGroup, isLeaving };
}
